/* eslint-disable @typescript-eslint/no-unused-vars */
// import Export16 from '@carbon/icons-react/es/export/16';
import { ConfigProvider, Tooltip, Space as SpaceAtom } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { TableSkeleton } from 'components/atoms/table';
import { ProductiveHeading07 } from 'components/styles';
import {
  useIntervals,
  useKeyEvents,
} from 'utils/project-fields/use-project-fields';
import { Table } from '../table';
import { useExport } from './use-export';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--white-100);
  box-shadow: var(--shadow-02);
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-05);
  align-items: center;
`;

const Header = styled.h1`
  ${ProductiveHeading07};
  font-weight: var(--font-weight-bold) !important;
`;

export const OverviewViewTable = ({
  renderEmpty,
  filter,
  selectedRange,
  isLoading,
  intervalReportData,
  absoluteValueOptions,
  zoomLevel,
}) => {
  const events = useKeyEvents();
  const intervals = useIntervals();

  const intervalsMapping =
    intervals.reduce((acc, curr) => {
      return { ...acc, [curr.fieldId]: curr };
    }, {}) ?? {};

  const IntervalTooltip = ({ keyEventNames }) => (
    <SpaceAtom direction="vertical">
      <FormattedMessage id="project.overview.interval_tooltip.title" />
      <strong>
        <FormattedMessage
          id="project.overview.interval_tooltip.description"
          values={{
            firstInterval: keyEventNames[0],
            secondInterval: keyEventNames[1],
          }}
        />
      </strong>
    </SpaceAtom>
  );

  const IntervalWithTooltip = ({ keyEventNames, intervalName }) => {
    return (
      <Tooltip title={<IntervalTooltip keyEventNames={keyEventNames} />}>
        {intervalName}
      </Tooltip>
    );
  };

  const OverviewViewTableColumns = [
    {
      title: '',
      children: [
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_nameOfInterval" />
          ),
          dataIndex: 'nameOfInterval',
          fixed: 'left',
          width: 200,
        },
      ],
    },
    {
      title: (
        <FormattedMessage id="reports.intervals.table.column_title.overall" />
      ),
      children: [
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_noOfProjects" />
          ),
          dataIndex: 'noOfProjects',
          width: 150,
        },
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_benchmark" />
          ),
          dataIndex: 'benchmark',
          width: 100,
        },
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_median" />
          ),
          dataIndex: 'median',
          width: 100,
        },
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_average" />
          ),
          dataIndex: 'average',
          width: 100,
        },
      ],
    },
    {
      title:
        zoomLevel === 'monthly' ? (
          <FormattedMessage id="reports.intervals.table.column_monthlyBreakup" />
        ) : (
          <FormattedMessage id="reports.intervals.table.column_weeklyBreakup" />
        ),
      children: absoluteValueOptions.xAxis.categories.map((item) => ({
        title: item,
        key: item,
        dataIndex: item,
        width: 100,
      })),
    },
  ];

  const tableData = intervalReportData
    .filter((item) => Number(item.projectIds.length) > 0)
    ?.filter((data) =>
      Object.keys(intervalsMapping).includes(String(data.intervalId)),
    )
    .map((item) => {
      const sData =
        absoluteValueOptions.series.find((item1) => item1.name === item.key)
          ?.data ?? [];
      const mappedData = {};

      const { categories } = absoluteValueOptions.xAxis;
      let index = 0;

      while (index < categories.length) {
        mappedData[categories[index]] = sData[index]
          ? (sData[index].y && `${sData[index].y}d`) ?? '-'
          : '-';
        index += 1;
      }

      return {
        nameOfInterval: (
          <IntervalWithTooltip
            keyEventNames={[
              events.find(
                (event) =>
                  event.fieldId ===
                  intervalsMapping[item.intervalId]?.metaData?.startKeyEventId,
              )?.fieldLabel,
              events.find(
                (event) =>
                  event.fieldId ===
                  intervalsMapping[item.intervalId]?.metaData?.endKeyEventId,
              )?.fieldLabel,
            ]}
            intervalName={item.key}
          />
        ),
        noOfProjects: item.projectIds.length,
        benchmark: `${item.benchmark}d`,
        median: `${item.median}d`,
        average: `${item.average}d`,
        ...mappedData,
      };
    });

  const filteredProcessedData = tableData.filter(
    (data) => data.noOfProjects !== '-',
  );

  const { exportAsCSV } = useExport(selectedRange, filter);

  if (isLoading) {
    return <TableSkeleton columnCount={8} />;
  }

  return (
    <Wrapper>
      <HeaderWrapper>
        <Header>
          <FormattedMessage id="interval_reports.table.header" />
        </Header>
        {/* <Tooltip
          title={
            <FormattedMessage id="reports.users_performance.export.tooltip" />
          }
          placement="right"
        >
          <Button
            icon={<Export16 />}
            type="text"
            size="small"
            onClick={exportAsCSV}
          />
        </Tooltip> */}
      </HeaderWrapper>
      <ConfigProvider renderEmpty={renderEmpty}>
        <Table
          columns={OverviewViewTableColumns}
          dataSource={filteredProcessedData}
          pagination={false}
          scroll={filteredProcessedData.length > 0 ? { x: 1300 } : {}}
          bordered
        />
      </ConfigProvider>
    </Wrapper>
  );
};
